import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 65. CACHE
// Une couche de cache pour les données adaptées. On ne met JAMAIS en cache
// sans précaution :
//   · disponibilité temps réel ;
//   · données privées sensibles ;
//   · autorisations.
// Le cache est TENANT-AWARE : chaque clé est préfixée par l’organisation
// pour empêcher toute fuite entre entreprises.
// ─────────────────────────────────────────────────────────────

export const CACHE_SCOPES: { key: string; label: string; icon: string; cacheable: boolean; ttl: number; desc: string }[] = [
  { key: 'catalog', label: 'Catalogue', icon: 'ri-shopping-bag-3-line', cacheable: true, ttl: 3600, desc: 'Produits, catégories, services — peu changeants.' },
  { key: 'profile', label: 'Profil public', icon: 'ri-store-2-line', cacheable: true, ttl: 600, desc: 'Profil business affiché publiquement.' },
  { key: 'dashboard', label: 'Tableau de bord', icon: 'ri-dashboard-line', cacheable: true, ttl: 300, desc: 'Agrégats de tableau de bord.' },
  { key: 'settings', label: 'Paramètres', icon: 'ri-settings-3-line', cacheable: true, ttl: 600, desc: 'Paramètres résolus en cascade.' },
  { key: 'reports', label: 'Rapports', icon: 'ri-bar-chart-2-line', cacheable: true, ttl: 1800, desc: 'Rapports et statistiques lourds.' },
  { key: 'search', label: 'Recherche', icon: 'ri-search-line', cacheable: true, ttl: 120, desc: 'Résultats de recherche fréquents.' },
  { key: 'availability', label: 'Disponibilité temps réel', icon: 'ri-time-line', cacheable: false, ttl: 0, desc: 'JAMAIS mis en cache (temps réel).' },
  { key: 'permissions', label: 'Autorisations', icon: 'ri-shield-keyhole-line', cacheable: false, ttl: 0, desc: 'JAMAIS mis en cache (sécurité).' },
  { key: 'private_sensitive', label: 'Données privées sensibles', icon: 'ri-lock-line', cacheable: false, ttl: 0, desc: 'JAMAIS mis en cache (confidentialité).' },
];

export function cacheScopeMeta(key: string | null | undefined) {
  return CACHE_SCOPES.find((s) => s.key === key) || CACHE_SCOPES[0];
}

/** Règles de non-mise-en-cache (spec 65). */
export const NEVER_CACHE_RULES: { key: string; label: string; icon: string; reason: string }[] = [
  { key: 'availability', label: 'Disponibilité temps réel', icon: 'ri-time-line', reason: 'Un créneau libre doit rester exact à la seconde près.' },
  { key: 'private_sensitive', label: 'Données privées sensibles', icon: 'ri-lock-line', reason: 'Ne jamais conserver de données personnelles sensibles en cache.' },
  { key: 'permissions', label: 'Autorisations', icon: 'ri-shield-keyhole-line', reason: 'Une autorisation ne doit jamais être servie depuis un cache périmé.' },
];

export interface CoreCacheEntry {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  cache_key: string;
  scope: string | null;
  value: unknown;
  ttl_seconds: number | null;
  expires_at: string | null;
  tags: string[] | null;
  is_private: boolean | null;
  cacheable: boolean | null;
  hit_count: number | null;
  created_at: string | null;
  updated_at: string | null;
}

/** Préfixe la clé par l’organisation → isolation stricte entre tenants. */
export function tenantAwareKey(organizationId: number, key: string): string {
  return `org:${organizationId}:${key.replace(/^org:\d+:/, '')}`;
}

/** Le scope est-il autorisé à être mis en cache ? */
export function isCacheable(scope: string, isPrivate = false): boolean {
  const meta = cacheScopeMeta(scope);
  if (!meta.cacheable) return false;
  if (isPrivate && scope === 'private_sensitive') return false;
  return true;
}

export async function listCacheEntries(organizationId: number): Promise<CoreCacheEntry[]> {
  const { data, error } = await supabase
    .from('core_cache_entries')
    .select('*')
    .eq('organization_id', organizationId)
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return (data || []).map((r) => ({ ...(r as CoreCacheEntry), tags: Array.isArray((r as CoreCacheEntry).tags) ? (r as CoreCacheEntry).tags : [] }));
}

export interface SetCacheInput {
  tenantId?: number | null;
  organizationId: number;
  key: string;
  scope: string;
  value: unknown;
  ttlSeconds?: number;
  tags?: string[];
  isPrivate?: boolean;
}

/** Écrit une entrée de cache (refuse les scopes jamais cachables). */
export async function setCacheEntry(input: SetCacheInput): Promise<boolean> {
  if (!isCacheable(input.scope, input.isPrivate)) return false;
  const ttl = input.ttlSeconds ?? cacheScopeMeta(input.scope).ttl;
  const now = Date.now();
  const fullKey = tenantAwareKey(input.organizationId, input.key);
  const existing = await supabase
    .from('core_cache_entries')
    .select('id, hit_count')
    .eq('organization_id', input.organizationId)
    .eq('cache_key', fullKey)
    .maybeSingle();
  const payload = {
    tenant_id: input.tenantId ?? null,
    organization_id: input.organizationId,
    cache_key: fullKey,
    scope: input.scope,
    value: (input.value ?? null) as Record<string, unknown> | null,
    ttl_seconds: ttl,
    expires_at: new Date(now + ttl * 1000).toISOString(),
    tags: input.tags ?? [],
    is_private: input.isPrivate ?? false,
    cacheable: true,
    updated_at: new Date().toISOString(),
  };
  if (existing.data?.id) {
    const { error } = await supabase.from('core_cache_entries').update(payload).eq('id', existing.data.id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('core_cache_entries').insert(payload);
    if (error) throw error;
  }
  return true;
}

/** Lit une entrée valide (non expirée) et incrémente le hit count. */
export async function getCacheEntry(organizationId: number, key: string): Promise<unknown | null> {
  const fullKey = tenantAwareKey(organizationId, key);
  const { data } = await supabase
    .from('core_cache_entries')
    .select('id, value, expires_at, hit_count')
    .eq('organization_id', organizationId)
    .eq('cache_key', fullKey)
    .maybeSingle();
  if (!data) return null;
  if (data.expires_at && new Date(data.expires_at).getTime() < Date.now()) return null;
  await supabase.from('core_cache_entries').update({ hit_count: (data.hit_count ?? 0) + 1 }).eq('id', data.id);
  return data.value;
}

export async function invalidateCacheKey(organizationId: number, key: string): Promise<void> {
  const fullKey = tenantAwareKey(organizationId, key);
  const { error } = await supabase.from('core_cache_entries').delete().eq('organization_id', organizationId).eq('cache_key', fullKey);
  if (error) throw error;
}

export async function invalidateCacheByTag(organizationId: number, tag: string): Promise<void> {
  const { error } = await supabase.from('core_cache_entries').delete().eq('organization_id', organizationId).contains('tags', [tag]);
  if (error) throw error;
}

export async function purgeExpiredCache(organizationId: number): Promise<void> {
  const { error } = await supabase
    .from('core_cache_entries')
    .delete()
    .eq('organization_id', organizationId)
    .lt('expires_at', new Date().toISOString());
  if (error) throw error;
}

export interface CacheStats {
  entries: number;
  hits: number;
  hitRate: number;
  expired: number;
  scopes: number;
}

export async function fetchCacheStats(organizationId: number): Promise<CacheStats> {
  const entries = await listCacheEntries(organizationId);
  const totalHits = entries.reduce((acc, e) => acc + (e.hit_count ?? 0), 0);
  const expired = entries.filter((e) => e.expires_at && new Date(e.expires_at).getTime() < Date.now()).length;
  return {
    entries: entries.length,
    hits: totalHits,
    hitRate: entries.length > 0 ? Math.min(99, Math.round((totalHits / (totalHits + entries.length)) * 100)) : 0,
    expired,
    scopes: new Set(entries.map((e) => e.scope || 'other')).size,
  };
}

// ─────────────────────────────────────────────────────────────
// Consommation de la couche de cache par les MODULES LOURDS
// Le cache n’a de valeur que s’il est réellement consommé. Les modules
// lourds (catalogue, rapports, tableau de bord) lisent désormais leurs
// agrégats « à travers » le cache : hit → instantané, miss → calcul + mise
// en cache. Une invalidation automatique est déclenchée par les événements
// du socle (spec 39) — jamais de cache périmé après une écriture métier.
// ─────────────────────────────────────────────────────────────

export interface CacheConsumer {
  scope: string;
  keyPrefix: string;
  modules: string[];
  desc: string;
}

/** Registre des modules qui consomment le cache (documentation vivante). */
export const CACHE_CONSUMERS: CacheConsumer[] = [
  { scope: 'catalog', keyPrefix: 'catalog:', modules: ['Commerce', 'Boutique publique', 'POS', 'Inventory'], desc: 'Liste produits/services de la vitrine.' },
  { scope: 'reports', keyPrefix: 'report:', modules: ['Finance', 'Analytics', 'BI', 'Observabilité'], desc: 'Agrégats de rapports et statistiques lourdes.' },
  { scope: 'dashboard', keyPrefix: 'dashboard:', modules: ['Dashboard Core'], desc: 'Synthèse du tableau de bord central.' },
];

/**
 * Lecture traversante (read-through) : renvoie l’entrée de cache si elle est
 * valide, sinon exécute le loader, met le résultat en cache et le renvoie.
 * Le cache ne casse JAMAIS le métier : toute erreur retombe sur le loader.
 */
export async function getOrSetCache<T>(input: {
  organizationId: number;
  tenantId?: number | null;
  /** Clé logique SANS préfixe tenant (ajouté automatiquement). */
  key: string;
  scope: string;
  ttlSeconds?: number;
  tags?: string[];
  isPrivate?: boolean;
  loader: () => Promise<T>;
}): Promise<T> {
  // 1. Tentative de lecture du cache (best-effort).
  try {
    const cached = await getCacheEntry(input.organizationId, input.key);
    if (cached !== null && cached !== undefined) return cached as T;
  } catch {
    /* cache indisponible : on recalcule */
  }

  // 2. Calcul de la valeur fraîche.
  const fresh = await input.loader();

  // 3. Mise en cache (best-effort — jamais bloquant).
  try {
    await setCacheEntry({
      tenantId: input.tenantId ?? null,
      organizationId: input.organizationId,
      key: input.key,
      scope: input.scope,
      value: fresh,
      ttlSeconds: input.ttlSeconds,
      tags: input.tags,
      isPrivate: input.isPrivate,
    });
  } catch {
    /* la mise en cache ne doit jamais casser la lecture */
  }

  return fresh;
}

/** Supprime toutes les entrées d’une organisation pour une liste de scopes. */
export async function invalidateScopes(organizationId: number, scopes: string[]): Promise<number> {
  if (scopes.length === 0) return 0;
  const { data } = await supabase
    .from('core_cache_entries')
    .select('id')
    .eq('organization_id', organizationId)
    .in('scope', scopes);
  const ids = (data || []).map((r) => (r as { id: number }).id);
  if (ids.length === 0) return 0;
  await supabase.from('core_cache_entries').delete().in('id', ids);
  return ids.length;
}

/**
 * Correspondance ÉVÉNEMENT du socle → cache à invalider.
 * Préfixe d’événement (order., product., …) vers scopes + tags concernés.
 */
export const CACHE_INVALIDATION_MAP: { prefix: string; scopes: string[]; tags: string[]; reason: string }[] = [
  { prefix: 'order.', scopes: ['dashboard', 'reports'], tags: ['dashboard', 'reports', 'orders'], reason: 'Nouvelle commande / vente.' },
  { prefix: 'payment.', scopes: ['dashboard', 'reports'], tags: ['dashboard', 'reports', 'finance'], reason: 'Paiement reçu.' },
  { prefix: 'invoice.', scopes: ['dashboard', 'reports'], tags: ['dashboard', 'reports', 'finance'], reason: 'Facture créée ou payée.' },
  { prefix: 'product.', scopes: ['catalog', 'dashboard'], tags: ['catalog', 'products'], reason: 'Catalogue produits modifié.' },
  { prefix: 'service.', scopes: ['catalog', 'dashboard'], tags: ['catalog', 'services'], reason: 'Catalogue services modifié.' },
  { prefix: 'category.', scopes: ['catalog', 'dashboard'], tags: ['catalog', 'categories'], reason: 'Catégories modifiées.' },
  { prefix: 'customer.', scopes: ['dashboard', 'reports'], tags: ['dashboard', 'reports', 'customers'], reason: 'Client ajouté ou modifié.' },
  { prefix: 'booking.', scopes: ['dashboard'], tags: ['dashboard', 'bookings'], reason: 'Réservation créée ou modifiée.' },
  { prefix: 'appointment.', scopes: ['dashboard'], tags: ['dashboard', 'appointments'], reason: 'Rendez-vous modifié.' },
  { prefix: 'review.', scopes: ['dashboard', 'catalog', 'reports'], tags: ['dashboard', 'reviews'], reason: 'Avis publié ou modéré.' },
  { prefix: 'module.', scopes: ['dashboard'], tags: ['dashboard', 'modules'], reason: 'Module activé ou désactivé.' },
  { prefix: 'flag.', scopes: ['dashboard'], tags: ['dashboard', 'flags'], reason: 'Feature flag modifié.' },
];

export interface CacheInvalidationResult {
  eventKey: string;
  scopes: string[];
  tags: string[];
  invalidated: number;
  reason: string;
}

/** Règle d’invalidation applicable à un événement (ou null si aucun cache concerné). */
export function invalidationRuleForEvent(eventKey: string | null | undefined) {
  if (!eventKey) return null;
  return CACHE_INVALIDATION_MAP.find((r) => eventKey.startsWith(r.prefix)) || null;
}

/**
 * Invalide automatiquement le cache concerné par un événement du socle.
 * Appelée par le câblage d’événements (coreEventHooks) après chaque écriture
 * métier. Best-effort : ne lève jamais.
 */
export async function invalidateForEvent(
  organizationId: number,
  eventKey: string | null | undefined,
): Promise<CacheInvalidationResult | null> {
  const rule = invalidationRuleForEvent(eventKey);
  if (!rule) return null;
  let invalidated = 0;
  try {
    for (const tag of rule.tags) {
      try {
        await invalidateCacheByTag(organizationId, tag);
      } catch {
        /* tag ignoré */
      }
    }
    invalidated += await invalidateScopes(organizationId, rule.scopes);
  } catch {
    /* best-effort : l’invalidation ne doit jamais bloquer le métier */
  }
  return {
    eventKey: eventKey || '',
    scopes: rule.scopes,
    tags: rule.tags,
    invalidated,
    reason: rule.reason,
  };
}